import React from 'react'
import PropTypes from 'prop-types'

import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardHeader from '@material-ui/core/CardHeader'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Divider from '@material-ui/core/Divider'
import Tooltip from '@material-ui/core/Tooltip'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import Autorenew from '@material-ui/icons/Autorenew'
import CircularProgress from '@material-ui/core/CircularProgress'

import memoize from 'memoize-one'

import isNumber from 'lodash/isNumber'
import flatMap from 'lodash/flatMap'
import forEach from 'lodash/forEach'
import find from 'lodash/find'
import mergeWith from 'lodash/mergeWith'
import compact from 'lodash/compact'
import map from 'lodash/map'
import filter from 'lodash/filter'
import isEmpty from 'lodash/isEmpty'

/**
 * handleEmpty
 * @param  {Array} arr array to search
 * @param  {string} id  element id
 * @return {string}  element name or empty
 */
const handleEmpty = (arr, id) => {
  const data = find(arr, ['id', id])
  if (!data) {
    console.error('notFound', arr, id)
    return ''
  }
  return data.name
}

const _companyStats = memoize((farms, qualities, harvestSeasonStats) =>
  compact(flatMap(harvestSeasonStats, (value, farmId) => {
    // do not consider updatedAt key
    if (farmId === 'updatedAt') return null
    const farmName = handleEmpty(farms, farmId)
    // return if there is no farm: ie no permission
    if (!farmName) return
    // per farm object
    const farmSummary = {
      farmId,
      farmName,
      data: {}
    }

    // value has {$siteId: {$cropId: {$quality: $data, employees: [$employeeIds]}}}
    forEach(value, (siteData) => {
      // merge values and handle relevant fields
      mergeWith(farmSummary.data, siteData, (objValue, srcValue, key) => {
        // do not include time nor employees
        if (key === 'startTime' || key === 'endTime' || key === 'employees') {
          return null
        }
        // add quantity and weight values
        if (isNumber(srcValue)) {
          return (objValue || 0) + srcValue
        }
      })
    })
    // remove employees key
    delete farmSummary.data.employees
    // map data to relevant information
    farmSummary.dataSum = {}
    forEach(farmSummary.data, (cropData) => {
      forEach(cropData, (qualData, qualId) => {
        if (isEmpty(farmSummary.dataSum[qualId])) {
          farmSummary.dataSum[qualId] = qualData.weight
          farmSummary.dataSum[qualId].name = handleEmpty(qualities, qualId)
        } else {
          mergeWith(farmSummary.dataSum[qualId], qualData.weight, (objVal, srcVal) => objVal + srcVal)
        }
      })
    })
    // TODO: prepare dataSum to hanlde other units
    return farmSummary
  })))

const _secondaryText = memoize((input) => {
  const value = filter(input, (d) => d.name === 'iqf')
  if (isEmpty(value)) return ''
  return `${value[0].kg} kgs Iqf`
})

const _primaryText = memoize((input) => {
  const value = filter(input, (d) => d.name !== 'iqf')
  if (isEmpty(value)) return '0'
  return `${value[0].kg}`
})

const HarvestCompanyStatsCard = (props) => {
  const companyStats = _companyStats(props.farms, props.qualities, props.harvestSeasonStats)

  return (
    <Card>
      <CardHeader
        title={<Tooltip title='Hasta dia anterior de cosecha'><Button>Total temporada</Button></Tooltip>}
        action={
          props.statsCompute ? <CircularProgress />
            : <Tooltip title='Actualizar'>
              <IconButton onClick={() => props.computeCompanyStats((err) => err && window.alert('Error al actualizar'))}>
                <Autorenew />
              </IconButton>
            </Tooltip>
        }

      />
      <CardContent>
        <List>
          {
            map(companyStats, (value, key) =>
              <React.Fragment key={key}>
                <ListItem>
                  <ListItemText
                    primary={`${value.farmName}: ${new Intl.NumberFormat().format(_primaryText(value.dataSum))} kgs Export`}
                    secondary={`${_secondaryText(value.dataSum)}`}
                  />
                </ListItem>
                <Divider />
              </React.Fragment>
            )
          }
        </List>
      </CardContent>
      {/* TODO: action button in order to go to other seasons */}
    </Card>
  )
}

HarvestCompanyStatsCard.propTypes = {
  qualities: PropTypes.array.isRequired,
  farms: PropTypes.array.isRequired,
  harvestSeasonStats: PropTypes.object.isRequired,
  computeCompanyStats: PropTypes.func.isRequired,
  statsCompute: PropTypes.bool
}

export default HarvestCompanyStatsCard
